import { Group } from '@visx/group';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { BarGroup, BarRounded } from '@visx/shape';
import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';
import { Grid } from '@visx/grid';
import ChartTooltip from '../../components/ChartTooltip';
import { TooltipData } from '../../components/ChartTooltip/ChartTooltip';
import {
  FormatUtils,
  MAP_CHART_FIELDS_LABEL,
  METRICS,
  useWindowSize,
  isReach
} from '../../utils';
import styled from 'styled-components';
import uniqid from 'uniqid';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';
import ColumnChartImage from '../../components/ChartsImages/ColumnChartImage';
import ColumnTooltipContent from './components/ColumnTooltipContent';
const StyledMultipleColumnChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #ffffff;
  padding: 10px;
`;

type MultipleColumnChartProps = {
  margin: any;
  data: any[];
  yAxis: string;
  zAxis: string;
  color: string;
  emptyDataMessage: string;
  showValuesOn100: boolean;
  limit: any;
  showItemsPreview?: boolean;
  colors: any;
};
const defaultMargin = { top: 50, right: 50, bottom: 50, left: 100 };
let tooltipTimeout: number;
const MultipleColumnChart: FC<MultipleColumnChartProps> = ({
  data,
  yAxis,
  zAxis,
  limit,
  margin = defaultMargin,
  showItemsPreview,
  showValuesOn100,
  emptyDataMessage,
  colors
}) => {
  const svgRef = useRef(null);
  const chartId = useRef(uniqid());
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [tooltipType, setTooltipType] = useState('single');
  const isEmpty = !data || data.length === 0;
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip<TooltipData>();
  const windowSize = useWindowSize();

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );
  const keys = (!isEmpty && Object.keys(data[0].metrics)) || [];

  const colorScale = useMemo(
    () =>
      scaleOrdinal<string, string>({
        domain: Object.keys(colors),
        range: Object.values(colors)
      }),
    [colors]
  );

  const filteredData = useMemo(() => {
    if (isEmpty) {
      return [];
    }

    const columnData = data.map((d) => {
      let bars = [];
      if (d && d.metrics) {
        const sum = [
          ...keys.map((targetId) => d.metrics[targetId][yAxis])
        ].reduce((total, value) => total + value, 0);
        bars = keys.map((target) => {
          return {
            color: colors[target],
            width: sum > 0 ? (d.metrics[target][yAxis] / sum) * 100 : 0
          };
        });
      }

      return {
        ...d,
        bars,
        values: keys.map((key) => {
          return {
            id: d.id,
            color: colors[key],
            ...d.metrics[key]
          };
        })
      };
    });
    return limit !== 'auto' ? columnData.slice(0, limit) : columnData;
  }, [isEmpty, data, limit, yAxis, keys]);

  const barGroupData = useMemo(() => {
    return filteredData.map((d) => {
      const item = { id: d.id };
      keys.forEach((key) => {
        item[key] = d.metrics[key][yAxis];
      });
      return item;
    });
  }, [filteredData, keys, yAxis, showValuesOn100]);

  const minMaxValues = useMemo(() => {
    return {
      yMin:
        isReach(yAxis) || showValuesOn100
          ? 0
          : Math.min(
              ...filteredData.map((d) =>
                Math.min(...keys.map((key) => d.metrics[key][yAxis]))
              )
            ),
      yMax: Math.max(
        ...filteredData.map((d) =>
          Math.max(...keys.map((key) => d.metrics[key][yAxis]))
        )
      ),
      zMin:
        isReach(yAxis) || showValuesOn100
          ? Math.min(
              ...filteredData.map((d) =>
                Math.min(...keys.map((key) => d.metrics[key][zAxis]))
              )
            )
          : 0,
      zMax:
        isReach(yAxis) || showValuesOn100
          ? Math.max(
              ...filteredData.map((d) =>
                Math.max(...keys.map((key) => d.metrics[key][zAxis]))
              )
            )
          : 0
    };
  }, [yAxis, zAxis, showValuesOn100, filteredData, keys]);

  const yzDiff = Math.max(
    minMaxValues.yMax - minMaxValues.yMin,
    minMaxValues.zMax - minMaxValues.zMin,
    0
  );
  const yzMin = Math.min(minMaxValues.yMin, minMaxValues.zMin);
  const yzMax = Math.max(minMaxValues.yMax, minMaxValues.zMax);
  const yDomain = useMemo(
    () => [
      yzMin - yzDiff / 3 < 0 ? 0 : yzMin - yzDiff / 3,
      showValuesOn100 || (yAxis !== METRICS.AFFINITY && yzMax + yzDiff / 3 > 1)
        ? 1
        : yzMax + yzDiff / 3
    ],
    [minMaxValues, yzDiff, showValuesOn100, yzMin, yzMax, yAxis]
  );

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: yDomain,
        range: [0, showValuesOn100 || yzMax > 1 ? 1 : yzMax],
        nice: true
      }),
    [yzMax, yDomain, showValuesOn100]
  );

  const x0Scale = useMemo(
    () =>
      scaleBand({
        domain: (filteredData && filteredData.map((item) => item.id)) || [],
        padding: 0.2
      }),
    [filteredData]
  );

  const x1Scale = useMemo(
    () =>
      scaleBand<string>({
        domain: keys,
        padding: 0.1
      }),
    [keys]
  );

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  x0Scale.rangeRound([0, xMax]);
  x1Scale.rangeRound([0, x0Scale.bandwidth()]);
  yScale.range([yMax, 0]);

  const getZValue = useCallback(
    (index) => {
      return Math.max(
        ...keys.map((key) => filteredData[index].metrics[key][zAxis])
      );
    },
    [zAxis, filteredData, keys]
  );

  const getZBar = useCallback(
    (index, x0) => {
      const zValue = getZValue(index);
      const barZWidth = x0Scale.bandwidth() + 5;
      const barZHeight = yMax - (yScale(zValue) ?? 0);
      const barZ = yMax - barZHeight;

      if (x0 > 0 && barZ > 0 && isReach(yAxis)) {
        return (
          <BarRounded
            x={x0 - 5}
            y={barZ}
            all
            radius={barZWidth / 4}
            width={barZWidth}
            height={barZHeight}
            fill='#0ABAA6'
            fillOpacity={0.4}
          />
        );
      }
    },
    [filteredData, getZValue, x0Scale, yScale]
  );

  const getTickInsightType = useCallback(
    (d) => {
      const item = filteredData.find((i) => i.id === d);
      return (item && item.insight_type) || '';
    },
    [filteredData]
  );

  const getTickImage = useCallback(
    (d) => {
      const item = filteredData.find((i) => i.id === d);
      return (item && item.pic) || '';
    },
    [filteredData]
  );

  const getTickLabel = useCallback(
    (d) => {
      const item = filteredData.find((i) => i.id === d);
      return (item && item.label) || '';
    },
    [filteredData]
  );

  const getGroupContent = useCallback((group, groups, index) => {
    const options = groups.tickLabelProps(group.value);
    const props = {
      ...options,
      x: group.to.x,
      y: 10 + group.to.y,
      index
    };

    return groups.tickComponent(props);
  }, []);

  const handleMouseLeave = useCallback(
    (event) => {
      tooltipTimeout = window.setTimeout(() => {
        hideTooltip();
        setTooltipType('single');
      }, 0);
    },
    [hideTooltip]
  );

  const handleMouseMove = useCallback(
    (event, index, targetId, color = '#269F49') => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      const eventSvgCoords = localPoint(event);
      setTooltipType('single');
      showTooltip({
        tooltipData: {
          ...filteredData[index].metrics[targetId],
          label: filteredData[index].label,
          color: color
        },
        tooltipTop: eventSvgCoords?.y,
        tooltipLeft: eventSvgCoords?.x
      });
    },
    [showTooltip, filteredData]
  );

  const handleImageMouseMove = useCallback(
    (event, index) => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      const eventSvgCoords = localPoint(svgRef.current, event);
      setTooltipType('multiple');
      showTooltip({
        tooltipData: {
          ...filteredData[index],
          color: '#c6c6c6'
        },
        tooltipTop: eventSvgCoords?.y,
        tooltipLeft: eventSvgCoords?.x
      });
    },
    [showTooltip, svgRef, filteredData]
  );

  const getTickComponent = useCallback(
    (d) => {
      const index = filteredData.findIndex((i) => i.id === d.id);
      return (
        <React.Fragment
          key={`multiple-column-${chartId.current}-category-${d.id}`}
        >
          <ColumnChartImage
            d={{ ...d, index }}
            color={colorScale(d.index)}
            showItemsPreview={showItemsPreview}
            chartId={chartId.current}
            onMouseMove={handleImageMouseMove}
            onMouseLeave={handleMouseLeave}
          />
        </React.Fragment>
      );
    },
    [
      showItemsPreview,
      chartId.current,
      filteredData,
      colorScale,
      handleImageMouseMove,
      handleMouseLeave
    ]
  );

  if (isEmpty) {
    return (
      <StyledMultipleColumnChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledMultipleColumnChartWrapper>
    );
  }

  return (
    <StyledMultipleColumnChartWrapper ref={chartRef}>
      <svg ref={svgRef} width={width} height={height}>
        <rect width={width} height={height} fill='#ffffff' rx={14} />
        <Grid
          top={margin.top}
          left={margin.left}
          xScale={x0Scale}
          yScale={yScale}
          width={xMax}
          height={yMax}
        />
        <Group top={margin.top} left={margin.left}>
          <BarGroup
            data={barGroupData}
            keys={keys}
            height={yMax}
            x0={(item) => item.id}
            x0Scale={x0Scale}
            x1Scale={x1Scale}
            yScale={yScale}
            color={colorScale}
          >
            {(barGroups) =>
              barGroups.map((barGroup) => (
                <Group
                  key={`multiple-column-${chartId.current}-bar-group-${barGroup.index}-${barGroup.x0}`}
                  left={barGroup.x0}
                >
                  {getZBar(barGroup.index, barGroup.bars[0].x)}
                  {barGroup.bars.map((bar) => {
                    return (
                      <React.Fragment
                        key={`multiple-column-${chartId.current}-bar-${barGroup.index}-${bar.index}-${bar.value}-${bar.key}`}
                      >
                        {bar.value && bar.value >= 0 && bar.height >= 0 && (
                          <BarRounded
                            x={bar.x}
                            y={bar.y}
                            width={bar.width}
                            height={bar.height}
                            radius={bar.width / 4}
                            all
                            fill={bar.color}
                            fillOpacity={isReach(yAxis) ? 0.7 : 1}
                            onMouseMove={(event) =>
                              handleMouseMove(
                                event,
                                barGroup.index,
                                bar.key,
                                bar.color
                              )
                            }
                            onMouseLeave={(event) => handleMouseLeave(event)}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Group>
              ))
            }
          </BarGroup>
          <AxisLeft
            label={MAP_CHART_FIELDS_LABEL[yAxis]}
            scale={yScale}
            stroke='#9493A6'
            tickStroke='#9493A6'
            tickFormat={(d: any) => FormatUtils.formattedValue(d, yAxis)}
            tickLabelProps={() => ({
              fill: '#9493A6',
              fontSize: 11,
              textAnchor: 'end',
              dy: '0.33em',
              dx: '-0.33em',
              fontWeight: 'bold'
            })}
            labelProps={{
              fill: 'black',
              textAnchor: 'middle',
              fontSize: 14,
              fontWeight: 'bold',
              dx: '-1.2em'
            }}
          />
          <AxisBottom
            top={yMax}
            scale={x0Scale}
            tickLabelProps={(d) => ({
              fontSize: '11px',
              pic: getTickImage(d),
              id: d,
              label: getTickLabel(d),
              insight_type: getTickInsightType(d)
            })}
            tickComponent={(d) => getTickComponent(d)}
          >
            {(axisGroups) => (
              <React.Fragment>
                {axisGroups.ticks.map((group, index) => {
                  return (
                    <Group
                      key={`multiple-column-${chartId.current}-axis-group-${index}`}
                    >
                      {getGroupContent(group, axisGroups, index)}
                    </Group>
                  );
                })}
                {/* <Line
                  from={axisGroups.axisFromPoint}
                  to={axisGroups.axisToPoint}
                  stroke='#9493A6'
                  strokeWidth='1'
                  strokeOpacity={0.5}
                /> */}
              </React.Fragment>
            )}
          </AxisBottom>
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip
          opened={tooltipOpen}
          top={tooltipTop || 0}
          left={tooltipLeft || 0}
          data={tooltipData}
        >
          <ColumnTooltipContent
            data={tooltipData}
            chartId={chartId}
            type={tooltipType}
          />
        </ChartTooltip>
      )}
    </StyledMultipleColumnChartWrapper>
  );
};

export default MultipleColumnChart;
